import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";
import type { AlertRuleActions } from "./alert-actions";

/**
 * HTTP-клиент правил алертов §6.5 запросного канала §7.6, в стиле
 * `density/favorites-api.ts`. `GET /api/alert-rules` отдаёт список целиком
 * в порядке заведения (`crates/daemon/src/http/alerts.rs::alert_rules_list`),
 * без обёртки — `Json(views)` сериализует массив `AlertRuleView` прямо в
 * тело ответа.
 *
 * Разбор терпимый и по одному правилу (ревью stage6-alert-sound, пункт 4):
 * вкладке из всей формы `AlertRuleView`/`AlertRuleSpec` нужны только `id` и
 * флаги `rule.sound`/`rule.notify`, остальные поля (триггер, рынок, биржа…)
 * ей неинтересны и не проверяются. Элемент без строкового `id` или без
 * булевых `rule.sound`/`rule.notify` отбрасывается поштучно — остальные
 * правила списка остаются рабочими.
 */

const ALERT_RULES_BASE = "/api/alert-rules";

/** Разбирает один элемент списка — `undefined`, если ему не хватает нужных полей. */
function parseAlertRuleActions(value: unknown): AlertRuleActions | undefined {
  if (!isRecord(value) || typeof value.id !== "string") {
    console.error("правило алерта без строкового id отброшено", value);
    return undefined;
  }
  const rule = value.rule;
  if (!isRecord(rule) || typeof rule.sound !== "boolean" || typeof rule.notify !== "boolean") {
    console.error("правило алерта без rule.sound/rule.notify отброшено", value);
    return undefined;
  }
  return { id: value.id, sound: rule.sound, notify: rule.notify };
}

/** Список правил алертов §6.5 предъявившего сессию пользователя, целиком. */
export type AlertRulesListResult =
  | { status: "ok"; rules: AlertRuleActions[] }
  | { status: "unauthorized" }
  | { status: "unavailable" };

export async function fetchAlertRules(): Promise<AlertRulesListResult> {
  let response: Response;
  try {
    response = await fetch(ALERT_RULES_BASE, {
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (!response.ok) {
    console.error("список правил алертов отклонён демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!Array.isArray(body)) {
      return { status: "unavailable" };
    }
    const rules: AlertRuleActions[] = [];
    for (const item of body) {
      const parsed = parseAlertRuleActions(item);
      if (parsed) {
        rules.push(parsed);
      }
    }
    return { status: "ok", rules };
  } catch {
    return { status: "unavailable" };
  }
}
